import * as THREE from 'three'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import type { SortCompleteEvent } from '@/plugins/gsplat/types/EventTypes'
import type { ISplatSorterData } from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'
import { SortWorkerCode } from './SplatSortWorker'

const log = getLoggerManager().getLogger('gsplat:sort')

const sorterMemoryTotals = {
  workersCreated: 0,
  workersTerminated: 0,
  blobUrlsCreated: 0,
  blobUrlsRevoked: 0,
}

/** 普通排序器的进程级 Worker/Blob URL 平衡快照。 */
export interface SplatSorterMemoryDiagnostics {
  workersCreated: number
  workersTerminated: number
  workersActive: number
  blobUrlsCreated: number
  blobUrlsRevoked: number
  blobUrlsActive: number
}

export function getSplatSorterMemoryDiagnostics(): Readonly<SplatSorterMemoryDiagnostics> {
  return {
    ...sorterMemoryTotals,
    workersActive: sorterMemoryTotals.workersCreated - sorterMemoryTotals.workersTerminated,
    blobUrlsActive: sorterMemoryTotals.blobUrlsCreated - sorterMemoryTotals.blobUrlsRevoked,
  }
}

// 复用临时对象，避免每帧分配内存
const _invModelMatrix = new THREE.Matrix4()
const _modelCameraPos = new THREE.Vector3()
const _modelCameraDir = new THREE.Vector3()

/**
 * 排序器配置选项
 */
export interface SorterOptions {
  /** 排序完成回调 */
  onSortComplete?: (data: SortCompleteEvent) => void
  /** Keep a main-thread copy of centers for setMapping(); disable for large one-shot SOG data. */
  retainCentersForMapping?: boolean
}

/**
 * 高性能 Splat 排序器（对齐 PlayCanvas GSplatSorter）
 *
 * 核心特性：
 * - **Linear Sort**：基于相机方向投影的深度排序
 * - **Chunk 优化**：每 256 个 splat 一组，预计算包围球
 * - **基于直方图的精度分配**：根据 chunk 分布动态分配排序精度
 * - **AABB 优化**：使用 8 角投影快速计算距离范围
 * - **后方剔除**：二分查找快速定位相机背后的 splat
 * - **双缓冲机制**：零内存分配的 buffer 交换
 * - **Mapping 支持**：LOD 子集索引重映射
 *
 * 性能基准：
 * - 100 万 splat: ~10-15ms
 */
export class SplatSorter {
  private worker: Worker | null = null
  private orderTexture: THREE.DataTexture

  // PlayCanvas 风格：Buffer 复用池
  private orderBufferPool: Uint32Array[] = []

  // 裁剪后的可见索引（如果有）
  private culledIndices: Uint32Array | null = null

  // 回调函数
  private onSortCompleteCallback?: (data: SortCompleteEvent) => void

  // Chunk 优化：存储原始 centers 数据用于 mapping
  private originalCenters: Float32Array | null = null
  private readonly retainCentersForMapping: boolean

  // 最近一次排序耗时（毫秒）
  private lastSortTimeMs = 0

  /**
   * 构造函数
   * @param splatData 原始 Splat 数据（支持 SplatData 或任何实现 ISplatSorterData 接口的对象）
   * @param orderTexture 排序索引纹理（引用）
   * @param options 排序配置
   */
  constructor(
    splatData: SplatData | ISplatSorterData,
    orderTexture: THREE.DataTexture,
    options: SorterOptions = {}
  ) {
    this.orderTexture = orderTexture
    this.retainCentersForMapping = options.retainCentersForMapping ?? true
    if (options.onSortComplete) {
      this.onSortCompleteCallback = options.onSortComplete
    }

    // PlayCanvas: 从纹理获取完整的 buffer（包括填充区域）
    const textureData = orderTexture.image.data as Uint32Array
    const initialBuffer = textureData.slice()

    // 初始化为顺序索引
    const n = splatData.numSplats
    for (let i = 0; i < n; i++) {
      initialBuffer[i] = i
    }

    this.orderBufferPool.push(initialBuffer)

    // 创建 Worker
    this.createWorker(splatData)
  }

  /**
   * 创建并初始化 Web Worker
   */
  private createWorker(splatData: SplatData | ISplatSorterData): void {
    // 内联 Worker 代码
    const blob = new Blob([SortWorkerCode], { type: 'application/javascript' })
    const blobURL = URL.createObjectURL(blob)
    sorterMemoryTotals.blobUrlsCreated++
    try {
      this.worker = new Worker(blobURL)
      sorterMemoryTotals.workersCreated++
    } finally {
      URL.revokeObjectURL(blobURL)
      sorterMemoryTotals.blobUrlsRevoked++
    }

    // 获取 centers 数据
    const centers = splatData.getCenters()

    // 保存原始 centers 供 mapping 使用
    this.originalCenters = this.retainCentersForMapping ? centers.slice() : null

    // 从池中获取初始 buffer
    const orderBuffer = this.orderBufferPool.pop()!

    // 初始化 Worker（PlayCanvas 风格）
    this.worker.postMessage(
      {
        centers: centers.buffer,
        order: orderBuffer.buffer,
      },
      [centers.buffer, orderBuffer.buffer]
    )

    // 接收排序结果
    this.worker.onmessage = (e) => {
      this.onSortComplete(e.data)
    }

    this.worker.onerror = (e) => {
      log.error(`[ThreeGS] Sorter Worker Error: ${e.message}`)
    }
  }

  /**
   * 设置裁剪后的可见索引
   * @param indices 八叉树剔除后的可见 splat 索引
   */
  setCulledIndices(indices: Uint32Array): void {
    this.culledIndices = indices
  }

  /**
   * 设置子集映射（用于 LOD 系统）
   *
   * PlayCanvas 风格：当渲染部分 splats 时（如 LOD），需要将索引映射回原始 splat ID。
   *
   * @param mapping 原始 splat 索引数组，null 表示恢复完整数据
   */
  setMapping(mapping: Uint32Array | null): void {
    if (!this.originalCenters) {
      log.warn('[ThreeGS] Sorter mapping requires retained centers')
      return
    }

    if (mapping) {
      // 创建子集 centers
      const centers = new Float32Array(mapping.length * 3)
      for (let i = 0; i < mapping.length; i++) {
        const src = mapping[i] * 3
        const dst = i * 3
        centers[dst + 0] = this.originalCenters[src + 0]
        centers[dst + 1] = this.originalCenters[src + 1]
        centers[dst + 2] = this.originalCenters[src + 2]
      }

      // 发送到 Worker
      this.worker?.postMessage(
        {
          centers: centers.buffer,
          mapping: mapping.buffer,
        },
        [centers.buffer, mapping.buffer]
      )
    } else {
      // 恢复完整数据
      const centers = this.originalCenters.slice()
      this.worker?.postMessage(
        {
          centers: centers.buffer,
          mapping: null,
        },
        [centers.buffer]
      )
    }
  }

  /**
   * 设置相机并触发排序（对齐 PlayCanvas setCamera）
   *
   * @param camera 当前相机
   * @param meshMatrixWorld mesh 的世界矩阵
   */
  setCamera(camera: THREE.Camera, meshMatrixWorld?: THREE.Matrix4): void {
    // 获取世界空间相机参数
    const worldPos = camera.position
    const worldDir = camera.getWorldDirection(_modelCameraDir)

    // 抵消 Three.js Camera.getWorldDirection() 的 .negate()
    // 使其与 PlayCanvas 的行为一致（+Z 是前方）
    worldDir.negate()

    // 转换到模型空间
    let camPosX: number, camPosY: number, camPosZ: number
    let camDirX: number, camDirY: number, camDirZ: number

    if (meshMatrixWorld) {
      _invModelMatrix.copy(meshMatrixWorld).invert()
      _modelCameraPos.copy(worldPos).applyMatrix4(_invModelMatrix)
      _modelCameraDir.copy(worldDir).transformDirection(_invModelMatrix)

      camPosX = _modelCameraPos.x
      camPosY = _modelCameraPos.y
      camPosZ = _modelCameraPos.z
      camDirX = _modelCameraDir.x
      camDirY = _modelCameraDir.y
      camDirZ = _modelCameraDir.z
    } else {
      camPosX = worldPos.x
      camPosY = worldPos.y
      camPosZ = worldPos.z
      camDirX = worldDir.x
      camDirY = worldDir.y
      camDirZ = worldDir.z
    }

    // PlayCanvas 风格：直接发送相机参数
    const message: {
      cameraPosition: { x: number; y: number; z: number }
      cameraDirection: { x: number; y: number; z: number }
      culledIndices?: ArrayBuffer
    } = {
      cameraPosition: { x: camPosX, y: camPosY, z: camPosZ },
      cameraDirection: { x: camDirX, y: camDirY, z: camDirZ },
    }

    const transferList: ArrayBuffer[] = []

    // 如果有裁剪索引，添加到消息中
    if (this.culledIndices) {
      message.culledIndices = this.culledIndices.buffer as ArrayBuffer
      if (this.culledIndices.buffer instanceof ArrayBuffer) {
        transferList.push(this.culledIndices.buffer as ArrayBuffer)
      }
      this.culledIndices = null
    }

    this.worker?.postMessage(message, transferList)
  }

  /**
   * 排序完成回调（PlayCanvas 风格双缓冲）
   */
  private onSortComplete(data: { order: ArrayBuffer; count: number; timeMs: number }): void {
    // 1. 接收新的 order buffer
    const newOrder = new Uint32Array(data.order)

    // 2. 获取旧的 buffer
    const oldOrder = this.orderTexture.image.data as Uint32Array
    const oldBuffer = oldOrder.buffer

    // 3. 立即发送旧 buffer 回 Worker
    this.worker?.postMessage(
      {
        order: oldBuffer,
      },
      [oldBuffer]
    )

    // 4. 更新 texture 为新的排序结果
    this.orderTexture.image.data = newOrder
    this.orderTexture.needsUpdate = true

    // 触发排序完成回调
    if (this.onSortCompleteCallback) {
      this.onSortCompleteCallback({
        count: data.count,
        timeMs: data.timeMs,
      })
    }

    this.lastSortTimeMs = data.timeMs
  }

  /**
   * 获取最近一次排序耗时。
   *
   * @returns 最近一次排序的毫秒数；无结果时为 0
   */
  getLastSortTimeMs(): number {
    return this.lastSortTimeMs
  }

  /**
   * 释放资源
   */
  dispose(): void {
    if (this.worker) {
      this.worker.terminate()
      sorterMemoryTotals.workersTerminated++
    }
    this.worker = null
    this.orderBufferPool = []
  }
}
